import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../../api/client';
import { SkillCardNew } from './SkillCardNew';
import type { SkillMeta } from './types';
import { T } from '../../styles/tokens';
import { Button } from '../ui';

interface Props {
  search: string;
  onDetails: (s: SkillMeta) => void;
  onInstall: (s: SkillMeta) => void;
}

const PAGE_SIZE = 20;

export function TabMarketplace({ search, onDetails, onInstall }: Props) {
  const [allSkills, setAllSkills] = useState<SkillMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterCat, setFilterCat] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'category'>('name');

  useEffect(() => {
    api.get<any>('/cloud/all-skills')
      .then((r: any) => {
        const list = r?.skills || r?.data?.skills || r?.data || [];
        setAllSkills(Array.isArray(list) ? list : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Reset page on search/filter change
  useEffect(() => { setPage(1); }, [search, filterCat, sortBy]);

  const categories = [...new Set(allSkills.map(s => s.category || 'Geral'))].sort();

  let filtered = allSkills.filter(s => {
    if (filterCat && (s.category || 'Geral') !== filterCat) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || (s.description_pt || s.description || '').toLowerCase().includes(q);
  });

  // Sort
  filtered.sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    return (a.category || '').localeCompare(b.category || '');
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSkills = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (loading) return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} style={{ height: '200px', borderRadius: '16px', background: `linear-gradient(90deg, ${T.primaryBgSubtle} 25%, ${T.primaryBg} 50%, ${T.primaryBgSubtle} 75%)`, backgroundSize: '200% 100%', animation: 'skeletonShimmer 1.5s infinite' }} />
      ))}
    </div>
  );

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <select
          value={filterCat}
          onChange={(e: any) => setFilterCat(e.target.value)}
          style={{
            padding: '8px 14px', borderRadius: '10px',
            background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`,
            color: T.text, fontSize: '13px', outline: 'none',
          }}
        >
          <option value="" style={{ background: T.bg }}>Todas categorias</option>
          {categories.map(c => <option key={c} value={c} style={{ background: T.bg }}>{c}</option>)}
        </select>
        <select
          value={sortBy}
          onChange={(e: any) => setSortBy(e.target.value)}
          style={{
            padding: '8px 14px', borderRadius: '10px',
            background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`,
            color: T.text, fontSize: '13px', outline: 'none',
          }}
        >
          <option value="name" style={{ background: T.bg }}>Nome A-Z</option>
          <option value="category" style={{ background: T.bg }}>Categoria</option>
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '13px', color: T.textSecondary, alignSelf: 'center' }}>
          {filtered.length} skills
        </span>
      </div>

      {/* Grid */}
      {pageSkills.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: T.textSecondary }}>
 <div style={{ fontSize: '48px', marginBottom: '12px' }}></div>
          <div style={{ fontSize: '16px', fontWeight: 600 }}>Nenhuma skill encontrada</div>
          <div style={{ fontSize: '13px', marginTop: '8px' }}>Tente mudar os filtros ou a busca</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {pageSkills.map((s, i) => (
            <SkillCardNew key={s.name} skill={s} index={i} onDetails={onDetails} onInstall={onInstall} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '24px' }}>
          <Button variant="ghost"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: `1px solid ${T.purpleBgStrong}`,
              background: page === 1 ? 'transparent' : T.purpleBg,
              color: page === 1 ? T.textDisabled : T.purple, fontSize: '13px', fontWeight: 600,
              cursor: page === 1 ? 'not-allowed' : 'pointer',
            }}
          >
            ← Anterior
          </Button>
          <span style={{ fontSize: '13px', color: T.textSecondary }}>Página {page} de {totalPages}</span>
          <Button variant="ghost"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: `1px solid ${T.purpleBgStrong}`,
              background: page === totalPages ? 'transparent' : T.purpleBg,
              color: page === totalPages ? T.textDisabled : T.purple, fontSize: '13px', fontWeight: 600,
              cursor: page === totalPages ? 'not-allowed' : 'pointer',
            }}
          >
            Próximo →
          </Button>
        </div>
      )}
    </div>
  );
}
